import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  applyPreset,
  aspectLocked,
  clampTransform,
  clipBoxHeight,
  clipLabel,
  cycleFit,
  fitTextBox,
  resetAspect,
  rotateClip,
  setAspectLocked,
  setTextStyle,
  setTransform,
  snapTargets,
  sourceById,
  visualClipsAt,
  type Clip,
  type Project,
} from "./timeline.ts";
import { clipAtPoint, clipRect } from "./render.ts";
import { type PlayheadStore, usePlayheadDerived } from "./playhead.ts";

/**
 * The preview, plus the handles for moving and sizing what is in it.
 *
 * The pixels come from the canvas; the handles are ordinary DOM elements laid
 * over it. That split is deliberate: hit-testing, focus, keyboard access and
 * pointer capture all come free from the browser this way, and the canvas stays
 * a rendering surface with no interaction logic in it. The alternative — drawing
 * handles into the canvas and hit-testing them by hand — means reimplementing
 * all of that and getting the accessibility wrong.
 */

/** How the composition maps onto the canvas as displayed, in CSS pixels. */
interface Metrics {
  scale: number;
  offsetX: number;
  offsetY: number;
  /** The canvas as displayed, so overlays can be kept inside it. */
  stageWidth: number;
  stageHeight: number;
}

const IDLE: Metrics = { scale: 1, offsetX: 0, offsetY: 0, stageWidth: 0, stageHeight: 0 };

function useMetrics(ref: React.RefObject<HTMLCanvasElement | null>, project: Project): Metrics {
  const [metrics, setMetrics] = useState<Metrics>(IDLE);

  useLayoutEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const measure = () => {
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (width === 0 || height === 0) return;
      // The same letterbox arithmetic renderFrame uses, so the handles sit
      // exactly where the pixels do.
      const scale = Math.min(width / project.width, height / project.height);
      setMetrics({
        scale,
        offsetX: (width - project.width * scale) / 2,
        offsetY: (height - project.height * scale) / 2,
        stageWidth: width,
        stageHeight: height,
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [ref, project.width, project.height]);

  return metrics;
}

/**
 * The eight handles. Corners size both axes; edges size one.
 *
 * Edges only appear once the aspect ratio is unlocked, because with it locked
 * a single-axis handle would have to silently change the other one, and a
 * control that does something other than what it looks like is worse than a
 * control that is absent.
 */
type Handle = "nw" | "ne" | "sw" | "se" | "n" | "s" | "e" | "w";

const CORNERS: Handle[] = ["nw", "ne", "sw", "se"];
const EDGES: Handle[] = ["n", "s", "e", "w"];

interface DragState {
  mode: "move" | Handle;
  pointerX: number;
  pointerY: number;
  startX: number;
  startY: number;
  startScale: number;
  startHeight: number;
  /** A title's font size when the drag began, so a corner can scale the words with the box. */
  startSize: number;
}

/** Snap when within this many CSS pixels of a guide. */
const SNAP_PIXELS = 8;

/** The toolbar's own height plus a gap, and half its width. */
const TOOLBAR_CLEARANCE = 36;
const TOOLBAR_HALF_WIDTH = 80;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export function Stage({
  project,
  playhead,
  selected,
  canvasRef,
  interactive,
  onSelect,
  onChange,
}: {
  project: Project;
  playhead: PlayheadStore;
  selected: string | null;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  /** False while exporting, so handles cannot fight the export's seeking. */
  interactive: boolean;
  onSelect: (id: string | null) => void;
  onChange: (project: Project) => void;
}) {
  const metrics = useMetrics(canvasRef, project);
  const drag = useRef<DragState | null>(null);
  const [guides, setGuides] = useState<{ x: number[]; y: number[] }>({ x: [], y: [] });

  // Re-renders when the set of clips under the playhead changes, which is at
  // clip boundaries rather than on every step of the playhead.
  const visibleIds = usePlayheadDerived(playhead, (at) =>
    visualClipsAt(project, at).map((clip) => clip.id).join("\u0000"),
  );
  const visible = useMemo(() => {
    const ids = visibleIds ? visibleIds.split("\u0000") : [];
    return ids.map((id) => project.clips.find((clip) => clip.id === id)!).filter(Boolean);
  }, [project, visibleIds]);

  const toComposition = useCallback(
    (clientX: number, clientY: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };
      const box = canvas.getBoundingClientRect();
      return {
        x: (clientX - box.left - metrics.offsetX) / metrics.scale,
        y: (clientY - box.top - metrics.offsetY) / metrics.scale,
      };
    },
    [canvasRef, metrics],
  );

  /** Clicking the picture selects what is under the pointer, topmost first. */
  const onBackgroundPointerDown = (event: React.PointerEvent) => {
    if (!interactive) return;
    const point = toComposition(event.clientX, event.clientY);
    onSelect(clipAtPoint(project, playhead.now, point)?.id ?? null);
  };

  const begin = (clip: Clip, mode: DragState["mode"]) => (event: React.PointerEvent) => {
    if (!interactive) return;
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    onSelect(clip.id);
    const source = sourceById(project, clip.sourceId);
    drag.current = {
      mode,
      pointerX: event.clientX,
      pointerY: event.clientY,
      startX: clip.transform.x,
      startY: clip.transform.y,
      startScale: clip.transform.scale,
      startHeight: clipBoxHeight(project, clip, source),
      startSize: clip.text?.style.size ?? 0,
    };
  };

  const move = (clip: Clip) => (event: React.PointerEvent) => {
    const state = drag.current;
    if (!state) return;
    const source = sourceById(project, clip.sourceId);
    if (clip.kind === "video" && !source) return;

    const dx = (event.clientX - state.pointerX) / metrics.scale / project.width;
    const dy = (event.clientY - state.pointerY) / metrics.scale / project.height;

    if (state.mode === "move") {
      let x = state.startX + dx;
      let y = state.startY + dy;
      const targets = snapTargets(project, clip, source);
      const thresholdX = SNAP_PIXELS / metrics.scale / project.width;
      const thresholdY = SNAP_PIXELS / metrics.scale / project.height;
      const hitX = targets.x.find((target) => Math.abs(target - x) < thresholdX);
      const hitY = targets.y.find((target) => Math.abs(target - y) < thresholdY);
      if (hitX !== undefined) x = hitX;
      if (hitY !== undefined) y = hitY;
      setGuides({ x: hitX !== undefined ? [hitX] : [], y: hitY !== undefined ? [hitY] : [] });
      onChange(setTransform(project, clip.id, clampTransform({ x, y })));
      return;
    }

    // Sizing happens about the centre, so a handle moves its own side twice as
    // far as the pointer: the opposite side moves the other way by the same
    // amount. Anchoring to the opposite corner instead would need the position
    // to move as well, and the two corrections fight over the snap targets.
    const locked = aspectLocked(clip.transform);
    const patch: Parameters<typeof clampTransform>[0] = {};
    const wider = state.mode.includes("e") ? 1 : state.mode.includes("w") ? -1 : 0;
    const taller = state.mode.includes("s") ? 1 : state.mode.includes("n") ? -1 : 0;
    const corner = wider !== 0 && taller !== 0;

    /**
     * A title's corner scales the words along with the box, the way a sticker
     * scales, because that is what dragging a corner of some text means to
     * everyone who has done it in any other app. Its side handles change only
     * the box, so the words rewrap or the background gets taller; the font
     * stays put.
     */
    if (clip.kind === "text" && corner && locked) {
      const along = Math.abs(dx) > Math.abs(dy) ? wider * dx : taller * dy * (project.height / project.width);
      const scale = clamp(state.startScale + along * 2, 0.05, 4);
      const size = clamp(state.startSize * (scale / state.startScale), 0.01, 0.6);
      onChange(setTextStyle(setTransform(project, clip.id, { scale }), clip.id, { size }));
      return;
    }
    if (clip.kind === "text" && !corner) {
      if (wider !== 0) patch.scale = state.startScale + wider * dx * 2;
      if (taller !== 0) patch.height = state.startHeight + taller * dy * 2;
      onChange(setTransform(project, clip.id, clampTransform(patch)));
      return;
    }

    if (locked) {
      // One number to change, driven by whichever axis the pointer moved most,
      // so a corner drag follows the hand rather than only its horizontal part.
      const along = Math.abs(dx) > Math.abs(dy) ? wider * dx : taller * dy * (project.height / project.width);
      patch.scale = state.startScale + along * 2;
    } else {
      if (wider !== 0) patch.scale = state.startScale + wider * dx * 2;
      if (taller !== 0) patch.height = state.startHeight + taller * dy * 2;
    }
    onChange(setTransform(project, clip.id, clampTransform(patch)));
  };

  const end = () => {
    drag.current = null;
    setGuides({ x: [], y: [] });
  };

  // A clip nudged with the keyboard moves by a visible amount rather than a
  // pixel, because the composition may be far larger than the canvas.
  const nudge = (clip: Clip, dx: number, dy: number) =>
    onChange(
      setTransform(
        project,
        clip.id,
        clampTransform({ x: clip.transform.x + dx * 0.01, y: clip.transform.y + dy * 0.01 }),
      ),
    );

  useEffect(() => {
    if (!interactive) end();
  }, [interactive]);

  return (
    <div
      className="pointer-events-auto absolute inset-0"
      onPointerDown={onBackgroundPointerDown}
      role="presentation"
    >
      {guides.x.map((x) => (
        <span
          key={`gx${x}`}
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 top-0 w-px bg-teal-deep/70"
          style={{ left: metrics.offsetX + x * project.width * metrics.scale }}
        />
      ))}
      {guides.y.map((y) => (
        <span
          key={`gy${y}`}
          aria-hidden="true"
          className="pointer-events-none absolute left-0 right-0 h-px bg-teal-deep/70"
          style={{ top: metrics.offsetY + y * project.height * metrics.scale }}
        />
      ))}

      {visible.map((clip) => {
        const source = sourceById(project, clip.sourceId);
        if (clip.kind === "video" && !source) return null;
        const rect = clipRect(project, clip, source);
        const label = clipLabel(clip, source);
        const isSelected = selected === clip.id;
        const style: {
          left: number;
          top: number;
          width: number;
          height: number;
          transform?: string;
        } = {
          left: metrics.offsetX + rect.x * metrics.scale,
          top: metrics.offsetY + rect.y * metrics.scale,
          width: rect.w * metrics.scale,
          height: rect.h * metrics.scale,
          ...(clip.transform.rotation
            ? { transform: `rotate(${clip.transform.rotation}deg)` }
            : {}),
        };
        /**
         * The toolbar sits outside the rotated element.
         *
         * It used to be a child of it, and so inherited the rotation: turning
         * a clip on its side turned the toolbar on its side too, and left it
         * floating beside the clip rather than above it. Handles do belong
         * inside the rotation, because they mark the clip's own corners; the
         * toolbar belongs to the screen.
         */
        const bounds = rotatedBounds(
          style.width,
          style.height,
          clip.transform.rotation,
        );
        const centreX = style.left + style.width / 2;
        const boxTop = style.top + style.height / 2 - bounds.height / 2;
        const boxBottom = boxTop + bounds.height;
        // Above the box, or below when there is no room above. A clip larger
        // than the frame has room for neither, so the result is clamped into
        // the stage afterwards: a toolbar just outside a clip is nicer, but a
        // toolbar off the edge is unreachable.
        const preferredTop = boxTop < TOOLBAR_CLEARANCE ? boxBottom + 8 : boxTop - TOOLBAR_CLEARANCE;
        const toolbarTop = clamp(
          preferredTop,
          8,
          Math.max(8, metrics.stageHeight - TOOLBAR_CLEARANCE),
        );
        const toolbarLeft = clamp(
          centreX,
          TOOLBAR_HALF_WIDTH,
          Math.max(TOOLBAR_HALF_WIDTH, metrics.stageWidth - TOOLBAR_HALF_WIDTH),
        );
        return (
          <Fragment key={clip.id}>
            {isSelected && interactive ? (
              <div
                className="absolute z-20 -translate-x-1/2"
                style={{ left: toolbarLeft, top: toolbarTop }}
              >
                <ClipToolbar project={project} clip={clip} onChange={onChange} />
              </div>
            ) : null}
            <div className="absolute" style={style}>
            <button
              type="button"
              aria-label={`${label}: move`}
              onPointerDown={begin(clip, "move")}
              onPointerMove={move(clip)}
              onPointerUp={end}
              onPointerCancel={end}
              onKeyDown={(event) => {
                const step: Record<string, [number, number]> = {
                  ArrowLeft: [-1, 0],
                  ArrowRight: [1, 0],
                  ArrowUp: [0, -1],
                  ArrowDown: [0, 1],
                };
                const delta = step[event.key];
                if (!delta) return;
                event.preventDefault();
                nudge(clip, delta[0], delta[1]);
              }}
              className={`absolute inset-0 cursor-move rounded-[2px] ${
                isSelected
                  ? "outline outline-2 outline-offset-[-1px] outline-teal-deep"
                  : "outline-dashed outline-1 outline-offset-[-1px] outline-cream/40 hover:outline-cream/80"
              }`}
              style={interactive ? undefined : { pointerEvents: "none" }}
            />
            {isSelected && interactive
              ? handlesFor(clip).map((handle) => (
                  <button
                    key={handle}
                    type="button"
                    aria-label={`${label}: resize ${handle}`}
                    onPointerDown={begin(clip, handle)}
                    onPointerMove={move(clip)}
                    onPointerUp={end}
                    onPointerCancel={end}
                    className="absolute rounded-sm border border-pine-deep bg-cream"
                    style={handleStyle(handle)}
                  />
                ))
              : null}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}

/**
 * Which handles a clip offers.
 *
 * A picture with its aspect locked has only corners, since a single-axis
 * handle would have to change the other axis behind your back. A title's
 * width is its own thing — it decides where the words wrap — so its side
 * handles are always there; top and bottom appear once its height is fixed
 * rather than following the words.
 */
function handlesFor(clip: Clip): Handle[] {
  const locked = aspectLocked(clip.transform);
  if (clip.kind === "text") return [...CORNERS, "e", "w", ...(locked ? [] : (["n", "s"] as Handle[]))];
  return [...CORNERS, ...(locked ? [] : EDGES)];
}

/**
 * The axis-aligned box a rotated rectangle occupies.
 *
 * Needed because anything positioned against a rotated clip but not rotated
 * with it — the toolbar — has to be placed against what the rotation actually
 * covers on screen, not against the clip's own width and height.
 */
function rotatedBounds(
  width: number,
  height: number,
  degrees: number,
): { width: number; height: number } {
  if (!degrees) return { width, height };
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  return {
    width: width * cos + height * sin,
    height: width * sin + height * cos,
  };
}

/** Position, size and cursor for one handle. Edges are slim bars, corners squares. */
function handleStyle(handle: Handle): React.CSSProperties {
  const horizontal = handle === "e" || handle === "w";
  const vertical = handle === "n" || handle === "s";
  const cursor = vertical
    ? "ns-resize"
    : horizontal
      ? "ew-resize"
      : handle === "nw" || handle === "se"
        ? "nwse-resize"
        : "nesw-resize";

  const style: React.CSSProperties = { cursor };
  if (vertical) {
    style.left = "50%";
    style.marginLeft = -9;
    style.width = 18;
    style.height = 6;
    style[handle === "n" ? "top" : "bottom"] = -3;
  } else if (horizontal) {
    style.top = "50%";
    style.marginTop = -9;
    style.height = 18;
    style.width = 6;
    style[handle === "w" ? "left" : "right"] = -3;
  } else {
    style.width = 12;
    style.height = 12;
    if (handle.includes("w")) style.left = -6;
    if (handle.includes("e")) style.right = -6;
    if (handle.startsWith("n")) style.top = -6;
    if (handle.startsWith("s")) style.bottom = -6;
  }
  return style;
}

const FIT_LABEL: Record<Clip["transform"]["fit"], string> = {
  cover: "Cover: fills the box, crops the edges",
  contain: "Contain: whole picture, gaps at the sides",
  stretch: "Stretch: fills the box, distorts the picture",
};

/**
 * The controls that belong to the thing being manipulated rather than to a
 * panel: they sit on the selection, follow it around, and are the ones needed
 * mid-gesture. Everything slower lives in the inspector.
 */
function ClipToolbar({
  project,
  clip,
  onChange,
}: {
  project: Project;
  clip: Clip;
  onChange: (project: Project) => void;
}) {
  const locked = aspectLocked(clip.transform);
  const text = clip.kind === "text";
  return (
    <div className="flex items-center gap-0.5 rounded-full border border-rule bg-surface/95 px-1 py-0.5 shadow-[0_4px_10px_-4px_rgba(31,63,57,0.5)] backdrop-blur-sm">
      <ToolButton
        label={
          text
            ? locked
              ? "Height follows the words. Click for a fixed height"
              : "Fixed height. Click to let it follow the words"
            : locked
              ? "Unlock the aspect ratio"
              : "Lock the aspect ratio"
        }
        active={locked}
        onClick={() => onChange(setAspectLocked(project, clip.id, !locked))}
      >
        {locked ? <LockIcon /> : <UnlockIcon />}
      </ToolButton>
      {text ? (
        <ToolButton label="Shrink the box to the words" onClick={() => onChange(fitTextBox(project, clip.id))}>
          <RatioIcon />
        </ToolButton>
      ) : null}
      {locked || text ? null : (
        <>
          <ToolButton
            label="Back to the original proportions"
            onClick={() => onChange(resetAspect(project, clip.id))}
          >
            <RatioIcon />
          </ToolButton>
          <ToolButton label={FIT_LABEL[clip.transform.fit]} onClick={() => onChange(cycleFit(project, clip.id))}>
            <FitIcon mode={clip.transform.fit} />
          </ToolButton>
        </>
      )}
      <ToolButton label="Rotate 90°" onClick={() => onChange(rotateClip(project, clip.id))}>
        <RotateIcon />
      </ToolButton>
      <ToolButton
        label="Centre it in the frame"
        onClick={() => onChange(applyPreset(project, clip.id, "centre"))}
      >
        <CentreIcon />
      </ToolButton>
    </div>
  );
}

function ToolButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      // The toolbar sits inside the overlay, which begins a drag on pointerdown.
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      className={`flex size-6 items-center justify-center rounded-full transition-colors ${
        active ? "bg-pine text-cream" : "text-soft hover:bg-cream-2 hover:text-pine"
      }`}
    >
      {children}
    </button>
  );
}

const LockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const UnlockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 7.5-2" />
  </svg>
);

const RatioIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="6" width="18" height="12" rx="2" />
    <path d="M3 12h18" />
  </svg>
);

const RotateIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-3-6.7" />
    <path d="M21 4v5h-5" />
  </svg>
);

const CentreIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="8" y="8" width="8" height="8" rx="1" />
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
  </svg>
);

function FitIcon({ mode }: { mode: Clip["transform"]["fit"] }) {
  if (mode === "contain") {
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <rect x="3" y="8" width="18" height="8" rx="1" fill="currentColor" stroke="none" opacity="0.35" />
      </svg>
    );
  }
  if (mode === "stretch") {
    return (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="16" rx="2" />
        <path d="M7 12h10M7 12l2-2M7 12l2 2M17 12l-2-2M17 12l-2 2" />
      </svg>
    );
  }
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="2" fill="currentColor" stroke="none" opacity="0.35" />
      <rect x="3" y="4" width="18" height="16" rx="2" />
    </svg>
  );
}

export { applyPreset };
